/**
 * Segment-exchange protocol: peer <-> peer over a WebRTC DataChannel.
 *
 * Two message families travel on the same channel:
 *  - CONTROL messages: small JSON strings (have/request/cancel/reject/ping/pong).
 *  - DATA messages: binary ArrayBuffers carrying chunked segment bytes.
 *
 * Chunking is mandatory: a DataChannel message must stay well under the SCTP
 * limit (~16 KB is the safe interoperable ceiling), while HLS segments are
 * hundreds of KB. Every segment transfer is split into fixed-size chunks framed
 * by a compact binary header and reassembled on the far side by requestId.
 */

import type { SegmentId } from "./common.js";

/**
 * Application payload per DataChannel message (bytes). 64 KB − header: modern
 * browsers negotiate an SCTP maxMessageSize ≥ 256 KB, so larger chunks cut
 * per-chunk framing/dispatch overhead and lift throughput. Mixed chunk sizes
 * across the swarm interoperate — each frame carries its own index/total.
 */
export const CHUNK_PAYLOAD_BYTES = 64 * 1024 - 16;

// ---- control messages (JSON strings) ---------------------------------------

/** Announce which segments this peer currently holds (BitTorrent-style HAVE). */
export interface HaveMsg {
  readonly t: "have";
  /** Segments newly available since the last announce. */
  readonly add: SegmentId[];
  /** Segments dropped from the cache (e.g. slid out of the live window). */
  readonly drop?: SegmentId[];
}

/**
 * Ask a peer for a segment. `reqId` correlates the incoming DATA chunks.
 * For multi-peer swarming, `stride`/`offset` request only the chunks whose
 * index satisfies `index % stride === offset` (default: the whole segment).
 */
export interface RequestMsg {
  readonly t: "request";
  readonly reqId: number;
  readonly seg: SegmentId;
  readonly stride?: number;
  readonly offset?: number;
}

/**
 * Sent by the server after the last DATA chunk of a request: tells the requester
 * how many chunks it sent (`count`) and the segment's total chunk count
 * (`total`). Makes completion robust on an unordered channel (and covers the
 * zero-chunk case when a swarm offset lands past the end).
 */
export interface DoneMsg {
  readonly t: "done";
  readonly reqId: number;
  readonly total: number;
  readonly count: number;
}

/** Abort an in-flight request (e.g. HTTP fallback already satisfied it). */
export interface CancelMsg {
  readonly t: "cancel";
  readonly reqId: number;
}

/** Peer cannot serve the request (no longer cached / over capacity). */
export interface RejectMsg {
  readonly t: "reject";
  readonly reqId: number;
  readonly reason: "missing" | "busy";
}

/** RTT probe. `id` echoed back in pong; `ts` is the sender's monotonic clock. */
export interface PingMsg {
  readonly t: "ping";
  readonly id: number;
  readonly ts: number;
}

export interface PongMsg {
  readonly t: "pong";
  readonly id: number;
  readonly ts: number;
}

export type ControlMsg =
  | HaveMsg
  | RequestMsg
  | DoneMsg
  | CancelMsg
  | RejectMsg
  | PingMsg
  | PongMsg;

// ---- data frame (binary) ----------------------------------------------------

/** Fixed binary header prefixed to every chunk. Little-endian. */
export interface ChunkHeader {
  readonly reqId: number; // uint32
  readonly index: number; // uint16 — chunk position
  readonly total: number; // uint16 — total chunks in this transfer
  readonly byteLength: number; // uint32 — full segment size (redundant safety)
}

const HEADER_BYTES = 4 + 2 + 2 + 4; // reqId + index + total + byteLength

/** Encode one chunk into a single ArrayBuffer to send over the DataChannel. */
export function encodeChunk(header: ChunkHeader, payload: Uint8Array): ArrayBuffer {
  const buf = new ArrayBuffer(HEADER_BYTES + payload.byteLength);
  const view = new DataView(buf);
  view.setUint32(0, header.reqId, true);
  view.setUint16(4, header.index, true);
  view.setUint16(6, header.total, true);
  view.setUint32(8, header.byteLength, true);
  new Uint8Array(buf, HEADER_BYTES).set(payload);
  return buf;
}

export interface DecodedChunk {
  readonly header: ChunkHeader;
  readonly payload: Uint8Array;
}

/** Decode a received binary DataChannel message back into header + payload. */
export function decodeChunk(data: ArrayBuffer): DecodedChunk {
  const view = new DataView(data);
  const header: ChunkHeader = {
    reqId: view.getUint32(0, true),
    index: view.getUint16(4, true),
    total: view.getUint16(6, true),
    byteLength: view.getUint32(8, true),
  };
  const payload = new Uint8Array(data, HEADER_BYTES);
  return { header, payload };
}

/** Split a full segment into chunk payloads of at most CHUNK_PAYLOAD_BYTES. */
export function splitIntoChunks(data: Uint8Array): Uint8Array[] {
  const chunks: Uint8Array[] = [];
  for (let off = 0; off < data.byteLength; off += CHUNK_PAYLOAD_BYTES) {
    chunks.push(data.subarray(off, Math.min(off + CHUNK_PAYLOAD_BYTES, data.byteLength)));
  }
  // A zero-length segment still needs one (empty) chunk so `total` is well-defined.
  if (chunks.length === 0) chunks.push(new Uint8Array(0));
  return chunks;
}
